import React, { useEffect, useState } from 'react'
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    Box,
} from '@mui/material';
import SectionTitle from '../../../ui/sectionTitle';
import { useQuery } from 'react-query';
import { ValidDataService, LensConfigurationService } from '../../../../service/validDataService';
import CustomSelect from '../../../common/customSelect';


function ValidLensPropertiesForm({ formik }) {
    const {data: lensType, isLoading: lensTypeLoading, error: lensTypeError } = useQuery('lensTypes', ValidDataService.getLensTypes)
    const {data: lensMaterial, isLoading: lensMaterialLoading, error: lensMaterialError } = useQuery('lensMaterial', ValidDataService.getLensMaterialTypes)
    
    // New lens configuration dropdowns
    const [lensModels, setLensModels] = useState([]);
    const [lensConfigMaterials, setLensConfigMaterials] = useState([]);
    const [lensIndexes, setLensIndexes] = useState([]);
    const [lensTreatments, setLensTreatments] = useState([]);

    // Fetch lens configuration data
    useEffect(() => {
        // Fetch materials
        LensConfigurationService.getMaterialsWithLimit(0, 1000, [], []).then(result => {
            if (result?.code === 0) {
                setLensConfigMaterials(result.data?.data?.filter(m => m.status === 'active') || []);
            }
        }).catch(console.error);

        // Fetch indexes
        LensConfigurationService.getIndexesWithLimit(0, 1000, [], []).then(result => {
            if (result?.code === 0) {
                setLensIndexes(result.data?.data?.filter(i => i.status === 'active') || []);
            }
        }).catch(console.error);

        // Fetch treatments
        LensConfigurationService.getTreatmentsWithLimit(0, 1000, [], []).then(result => {
            if (result?.code === 0) {
                setLensTreatments(result.data?.data?.filter(t => t.status === 'active') || []);
            }
        }).catch(console.error);
    }, []);

    // Fetch lens models when manufacturer changes
    useEffect(() => {
        if (formik.values.manufacturerId) {
            LensConfigurationService.getModelsByManufacturer(formik.values.manufacturerId).then(result => {
                if (result?.code === 0) {
                    setLensModels(result.data?.filter(m => m.status === 'active') || []);
                }
            }).catch(console.error);
        } else {
            setLensModels([]);
            formik.setFieldValue('properties.lensModelId', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.manufacturerId]);

    return (
        <>
            {/* Section 3: Frame Properties */}
            <Grid item xs={12}>
                <SectionTitle title="Lens properties" />
            </Grid>

            <Grid container spacing={2}>
                {/* Section 3a: Dimensions */}
                <Grid item xs={12} sm={6}>
                    <Grid item xs={12} mt={2}>
                        <CustomSelect formik={formik} options={lensType?.data || []} isLoading={lensTypeLoading} value={formik.values.properties.lensType} error={lensTypeError} name="properties.lensType" label="Lens Type" />
                    </Grid>
                    
                    {/* Lens Model (from lens configuration) */}
                    <Grid item xs={12} mt={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Lens Model</InputLabel>
                            <Select
                                name="properties.lensModelId"
                                value={formik.values.properties?.lensModelId || ''}
                                onChange={(e) => formik.setFieldValue('properties.lensModelId', e.target.value)}
                                onBlur={formik.handleBlur}
                                label="Lens Model"
                                disabled={!formik.values.manufacturerId}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {lensModels.map((model) => (
                                    <MenuItem key={model.id} value={model.id}>
                                        {model.modelName} {model.manufacturerBrand?.brandName ? `(${model.manufacturerBrand.brandName})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Lens Material (from lens configuration) */}
                    <Grid item xs={12} mt={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Lens Material</InputLabel>
                            <Select
                                name="properties.lensMaterialId"
                                value={formik.values.properties?.lensMaterialId || ''}
                                onChange={(e) => formik.setFieldValue('properties.lensMaterialId', e.target.value)}
                                onBlur={formik.handleBlur}
                                label="Lens Material"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {lensConfigMaterials.map((material) => (
                                    <MenuItem key={material.id} value={material.id}>
                                        {material.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Lens Index */}
                    <Grid item xs={12} mt={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Lens Index</InputLabel>
                            <Select
                                name="properties.lensIndexId"
                                value={formik.values.properties?.lensIndexId || ''}
                                onChange={(e) => formik.setFieldValue('properties.lensIndexId', e.target.value)}
                                onBlur={formik.handleBlur}
                                label="Lens Index"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {lensIndexes.map((index) => (
                                    <MenuItem key={index.id} value={index.id}>
                                        {index.indexValue}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Old material field kept for backward compatibility */}
                    <Grid item xs={12} mt={1}>
                        <CustomSelect formik={formik} options={lensMaterial?.data || []} isLoading={lensMaterialLoading} value={formik.values.properties.material}  name="properties.material" error={lensMaterialError} label='Legacy Lens Material' />
                    </Grid>
                    
                    {/* <Grid item xs={12} mt={1}>
                    <CustomSelect formik={formik} options={['OU','OD','OS']} isLoading={false} value={formik.values.properties.lensOu} name="properties.lensOu" error={null} label='Lens' />
                    </Grid> */}
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.sphere}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.sphere`, event.target.value);
                              }}
                            name="sphere"
                            label="Sphere"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.sphere && Boolean(formik.errors.sphere)}
                            helperText={formik.touched.sphere && formik.errors.sphere}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.cyl}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.cyl`, event.target.value);
                              }}
                            name="cyl"
                            label="Cyl"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.cyl && Boolean(formik.errors.cyl)}
                            helperText={formik.touched.cyl && formik.errors.cyl}
                        />
                    </Grid>
                    {/* <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.axis}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.axis`, event.target.value);
                              }}
                            name="axis"
                            label="Axis"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.axis && Boolean(formik.errors.axis)}
                            helperText={formik.touched.axis && formik.errors.axis}
                        />
                    </Grid> */}
                </Grid>

                {/* Section 3b: Colors and Materials */}
                <Grid item xs={12} sm={6}>
                    {/* Lens Treatments Multi-Select */}
                    <Grid item xs={12} mt={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Lens Treatments</InputLabel>
                            <Select
                                multiple
                                name="properties.lensTreatmentIds"
                                value={formik.values.properties?.lensTreatmentIds || []}
                                onChange={(e) => formik.setFieldValue('properties.lensTreatmentIds', e.target.value)}
                                onBlur={formik.handleBlur}
                                label="Lens Treatments"
                                input={<OutlinedInput label="Lens Treatments" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const treatment = lensTreatments.find(t => t.id === value);
                                            return treatment ? (
                                                <Chip key={value} label={treatment.name} size="small" />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                            >
                                {/* Group treatments by category */}
                                {['coating', 'tint', 'photochromic', 'polarization', 'uv-protection', 'other'].map((category) => {
                                    const categoryTreatments = lensTreatments.filter(t => t.category === category);
                                    if (categoryTreatments.length === 0) return null;
                                    
                                    return [
                                        <MenuItem key={`header-${category}`} disabled sx={{ fontWeight: 'bold', fontSize: '0.85em', textTransform: 'uppercase' }}>
                                            {category.replace('-', ' ')}
                                        </MenuItem>,
                                        ...categoryTreatments.map((treatment) => (
                                            <MenuItem key={treatment.id} value={treatment.id}>
                                                <Checkbox checked={(formik.values.properties?.lensTreatmentIds || []).indexOf(treatment.id) > -1} />
                                                <ListItemText 
                                                    primary={treatment.name} 
                                                    secondary={treatment.isStackable ? 'Stackable' : ''} 
                                                />
                                            </MenuItem>
                                        ))
                                    ];
                                })}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.addPower}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.addPower`, event.target.value);
                              }}
                            name="addPower"
                            label="Add Power"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.addPower && Boolean(formik.errors.addPower)}
                            helperText={formik.touched.addPower && formik.errors.addPower}
                        />
                    </Grid>
                    {/* <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.bc}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.bc`, event.target.value);
                              }}
                            name="bc"
                            label="BC"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.bc && Boolean(formik.errors.bc)}
                            helperText={formik.touched.bc && formik.errors.bc}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.size}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.size`, event.target.value);
                              }}
                            name="size"
                            label="Size"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.size && Boolean(formik.errors.size)}
                            helperText={formik.touched.size && formik.errors.size}
                        />
                    </Grid>                    
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.ed}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.ed`, event.target.value);
                              }}
                            name="ed"
                            label="ED"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.ed && Boolean(formik.errors.ed)}
                            helperText={formik.touched.ed && formik.errors.ed}
                        />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                        <TextField
                            value={formik.values.properties.segHt}
                            onChange={(event) => {
                                formik.setFieldValue(`properties.segHt`, event.target.value);
                              }}
                            name="segHt"
                            label="Seg Ht"
                            type="number"
                            size='small'
                            fullWidth
                            error={formik.touched.segHt && Boolean(formik.errors.segHt)}
                            helperText={formik.touched.segHt && formik.errors.segHt}
                        />
                    </Grid> */}
                </Grid>
            </Grid>
        </>
    )
}

export default ValidLensPropertiesForm